import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { IconArrowLeft, IconInbox } from "@tabler/icons";
import { ToastHost } from "./toast";
import { inr, count } from "./format";

/* ------------------------------------------------------------------ */
/* Page chrome                                                         */
/* ------------------------------------------------------------------ */

// Every page wraps its content in <Page>: it adds the .pa token scope and a
// toast host, so a page also works outside the app shell.
export const Page = ({ children, className = "" }) => (
  <div className={`pa pa-page ${className}`}>
    {children}
    <ToastHost />
  </div>
);

// back: { to, label } (router link) or { onClick, label }.
export const PageHeader = ({ title, subtitle, back, meta, children }) => (
  <div className="pa-header">
    <div className="pa-header-text">
      {back &&
        (back.to ? (
          <Link className="pa-back" to={back.to}>
            <IconArrowLeft size={16} stroke={2} aria-hidden="true" />
            {back.label || "Back"}
          </Link>
        ) : (
          <button type="button" className="pa-back" onClick={back.onClick}>
            <IconArrowLeft size={16} stroke={2} aria-hidden="true" />
            {back.label || "Back"}
          </button>
        ))}
      <div className="pa-header-title">
        <h2>{title}</h2>
        {meta && <div className="pa-header-meta">{meta}</div>}
      </div>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {children && <div className="pa-header-actions">{children}</div>}
  </div>
);

// variant: primary | success | danger | danger-solid | ghost | link (default: neutral)
// size: sm | lg. block: full width. icon: any node (a tabler icon, or text).
export const Button = ({ variant, size, icon, iconRight, loading, block, disabled, children, className = "", ...props }) => (
  <button
    type="button"
    className={`pa-btn ${variant ? "pa-btn-" + variant : ""} ${size ? "pa-btn-" + size : ""} ${block ? "pa-btn-block" : ""} ${className}`}
    {...props}
    disabled={Boolean(loading || disabled)}
    aria-busy={loading ? true : undefined}
  >
    {loading ? <span className="pa-spin" aria-hidden="true" /> : icon ? <span className="pa-btn-glyph" aria-hidden="true">{icon}</span> : null}
    {children}
    {iconRight && !loading && <span className="pa-btn-glyph" aria-hidden="true">{iconRight}</span>}
  </button>
);

// Square icon button with a native tooltip (title) and aria-label. size: sm | lg.
export const IconButton = ({ label, children, variant, size, loading, disabled, className = "", ...props }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className={`pa-btn pa-btn-icon ${variant ? "pa-btn-" + variant : ""} ${size ? "pa-btn-icon-" + size : ""} ${className}`}
    {...props}
    disabled={Boolean(loading || disabled)}
    aria-busy={loading ? true : undefined}
  >
    {loading ? <span className="pa-spin" aria-hidden="true" /> : children}
  </button>
);

export const Spinner = ({ size = 16, label = "Loading", className = "" }) => (
  <span className={`pa-spinner ${className}`} role="status" aria-label={label} style={{ width: size, height: size }} />
);

export const Card = ({ title, subtitle, actions, children, className = "", style, padded = true }) => (
  <div className={`pa-card ${padded ? "" : "pa-card-flush"} ${className}`} style={style}>
    {(title || actions) && (
      <div className="pa-card-head">
        <div>
          {title && <h3 className="pa-card-title">{title}</h3>}
          {subtitle && <div className="pa-card-sub">{subtitle}</div>}
        </div>
        {actions && <div className="pa-card-actions">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* KPI cards                                                           */
/* ------------------------------------------------------------------ */

// tone: green | amber | red | blue | purple | gray. With onClick it becomes
// a toggle button (e.g. "show only these rows") and `active` highlights it.
export const StatCard = ({ label, value, sub, tone = "gray", icon, onClick, active, title, loading }) => {
  const body = (
    <>
      <div className="pa-kpi-top">
        <div className="pa-kpi-label">{label}</div>
        {icon && <span className="pa-kpi-icon" aria-hidden="true">{icon}</span>}
      </div>
      <div className="pa-kpi-value">{loading ? <span className="pa-skel" style={{ width: "60%", height: 24 }} /> : value}</div>
      {sub && <div className="pa-kpi-sub">{sub}</div>}
    </>
  );
  return onClick ? (
    <button type="button" className={`pa-kpi pa-kpi-${tone} ${active ? "is-active" : ""}`} onClick={onClick} aria-pressed={Boolean(active)} title={title}>
      {body}
    </button>
  ) : (
    <div className={`pa-kpi pa-kpi-${tone}`} title={title}>
      {body}
    </div>
  );
};

export const StatGrid = ({ children, min }) => (
  <div className="pa-kpis" style={min ? { gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` } : undefined}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Chips                                                               */
/* ------------------------------------------------------------------ */

export const Chip = ({ tone = "gray", dot, children, title }) => (
  <span className={`pa-chip pa-chip-${tone} ${dot ? "pa-chip-dot" : ""}`} title={title}>
    {children}
  </span>
);

const PAYMENT_STATUS = {
  0: ["Payable", "blue"],
  1: ["In progress", "amber"],
  2: ["Paid", "green"],
  3: ["Pending at bank", "amber"],
  4: ["Failed (bank)", "red"],
  5: ["Failed (system)", "red"],
  6: ["Cancelled", "gray"],
};
const SETTLEMENT = { 0: ["Unpaid", "gray"], 1: ["Partially paid", "amber"], 2: ["Settled", "green"] };
const REQUEST = { 0: ["Pending", "amber"], 1: ["Approved", "green"], 2: ["Partially approved", "blue"], 3: ["Rejected", "red"] };
// Order side status (m_order_payment_status / order_payment_status).
const ORDER_STATUS = {
  0: ["Unapproved", "gray"],
  1: ["Approved", "blue"],
  2: ["Payable", "purple"],
  3: ["Paid", "green"],
  4: ["Payment pending", "amber"],
  5: ["Payment failed", "red"],
  6: ["Cancelled", "gray"],
};
const RUN_STATUS = { 1: ["Open", "blue"], 2: ["Completed", "green"], 3: ["Cancelled", "gray"] };
const CERT_STATUS = { 1: ["Active", "green"], 0: ["Revoked", "gray"] };
const BUCKET = { PENDING: ["Pending", "amber"], PAYABLE: ["Payable", "blue"] };

// Ledger entry types (payment_ledger.entry_type), v2 plus the v1 types kept in backups.
export const ENTRY_TYPES = {
  ORDER_EARNED: ["Earned", "blue"],
  ORDER_AMOUNT_CHANGE: ["Amount change", "amber"],
  ORDER_VALIDATED: ["Validated", "green"],
  ORDER_UNVALIDATED: ["Unvalidated", "amber"],
  ORDER_CANCELLED: ["Cancelled", "gray"],
  ORDER_REVERSED: ["Reversed", "red"],
  ORDER_REINSTATED: ["Reinstated", "blue"],
  ORDER_REASSIGNED: ["Reassigned", "purple"],
  PAID_OUTSIDE: ["Paid outside", "gray"],
  PAID_OUTSIDE_REVERSAL: ["Paid outside undone", "gray"],
  TDS: ["TDS", "purple"],
  TDS_REVERSAL: ["TDS reversal", "purple"],
  TDS_RETAINED: ["TDS retained", "purple"],
  PAYOUT: ["Payout", "green"],
  PAYOUT_ENTRY: ["Manual payout", "blue"],
  PAYOUT_ENTRY_REVERSAL: ["Manual payout undone", "red"],
  LEGACY_DIFF: ["Legacy difference", "amber"],
  ADJUSTMENT: ["Adjustment", "amber"],
  // DealCoins (spec section 10): redeemed coins become payable cash (CR);
  // coins taken back after the cash was redeemed become a negative carry (DR).
  COIN_REDEMPTION: ["DealCoins redeemed", "green"],
  COIN_CLAWBACK: ["DealCoins clawback", "red"],
  REDEEM_APPROVED: ["Redeem approved (v1)", "gray"],
  REDEEM_REVERSAL: ["Redeem reversal (v1)", "gray"],
  OPENING_BALANCE: ["Opening balance (v1)", "gray"],
};

// order_ledger event types (the per-order audit trail).
export const ORDER_EVENTS = {
  ORDER_PUNCHED: ["Punched", "blue"],
  ORDER_VALIDATED: ["Validated", "green"],
  ORDER_UNVALIDATED: ["Unvalidated", "amber"],
  ORDER_AMOUNT_CHANGED: ["Amount changed", "amber"],
  ORDER_CANCELLED: ["Cancelled", "gray"],
  ORDER_REVERSED: ["Reversed", "red"],
  ORDER_REINSTATED: ["Reinstated", "blue"],
  ORDER_REASSIGNED: ["Reassigned", "purple"],
  ORDER_RELEASED: ["Released to payable", "blue"],
  PAID_OUTSIDE: ["Paid outside", "gray"],
  PAYOUT_BATCHED: ["Added to payout run", "purple"],
  REDEEM_REQUESTED: ["Redeem requested", "amber"],
  REDEEM_APPROVED: ["Redeem approved", "green"],
  REDEEM_REJECTED: ["Redeem rejected", "red"],
  HOLD: ["Put on hold", "red"],
  HOLD_RELEASED: ["Hold released", "green"],
  PAYMENT_PARTIAL: ["Partly paid", "amber"],
  PAYMENT_SETTLED: ["Paid", "green"],
  PAYMENT_CANCELLED: ["Payment cancelled", "gray"],
};

const humanize = (t) => String(t || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
const lookup = (map, key) => map[key] || [humanize(key), "gray"];

// [{ value, label }] lists for filters (SelectInput / FilterPanel multiselect).
export const entryTypeOptions = Object.keys(ENTRY_TYPES).map((value) => ({ value, label: ENTRY_TYPES[value][0] }));
export const orderEventOptions = Object.keys(ORDER_EVENTS).map((value) => ({ value, label: ORDER_EVENTS[value][0] }));

export const paymentStatusLabel = (s) => (PAYMENT_STATUS[s] || [String(s)])[0];
export const orderStatusLabel = (s) => (ORDER_STATUS[s] || [String(s)])[0];
export const entryTypeLabel = (t) => lookup(ENTRY_TYPES, t)[0];
export const entryTypeTone = (t) => lookup(ENTRY_TYPES, t)[1];
export const orderEventLabel = (t) => lookup(ORDER_EVENTS, t)[0];
export const orderEventTone = (t) => lookup(ORDER_EVENTS, t)[1];

const statusChip = (map, dot) => ({ status }) => {
  const [label, tone] = map[status] || [String(status), "gray"];
  return (
    <Chip tone={tone} dot={dot}>
      {label}
    </Chip>
  );
};

export const PaymentStatusChip = statusChip(PAYMENT_STATUS, true);
export const SettlementChip = statusChip(SETTLEMENT, false);
export const RequestStatusChip = statusChip(REQUEST, true);
export const OrderStatusChip = statusChip(ORDER_STATUS, true);
export const RunStatusChip = statusChip(RUN_STATUS, true);
export const CertStatusChip = statusChip(CERT_STATUS, false);
export const BucketChip = ({ bucket }) => {
  const [label, tone] = BUCKET[bucket] || [bucket, "gray"];
  return <Chip tone={tone}>{label}</Chip>;
};
export const EntryTypeChip = ({ type }) => {
  const [label, tone] = lookup(ENTRY_TYPES, type);
  return <Chip tone={tone}>{label}</Chip>;
};
export const OrderEventChip = ({ type }) => {
  const [label, tone] = lookup(ORDER_EVENTS, type);
  return <Chip tone={tone}>{label}</Chip>;
};
export const UserTypeChip = ({ type }) =>
  type === "M" ? <Chip tone="blue">Member</Chip> : type === "P" ? <Chip tone="purple">Publisher</Chip> : null;

/* ------------------------------------------------------------------ */
/* Quick tabs, progress, empty, callout, key/value                     */
/* ------------------------------------------------------------------ */

// Segmented quick filter. items: [{ key, label, count? }]
export const QuickTabs = ({ items, value, onChange, ariaLabel = "Quick filter" }) => (
  <div className="pa-tabs" role="tablist" aria-label={ariaLabel}>
    {items.map((it) => (
      <button
        key={it.key}
        type="button"
        role="tab"
        aria-selected={value === it.key}
        className={`pa-tab ${value === it.key ? "is-active" : ""}`}
        onClick={() => onChange(it.key)}
      >
        {it.label}
        {it.count !== undefined && it.count !== null && <span className="pa-tab-count">{count(it.count)}</span>}
      </button>
    ))}
  </div>
);

// Paid-vs-total bar. With `percent` it is a plain progress bar (value/total as counts).
export const Progress = ({ value, total, label = true, percent = false, tone }) => {
  const pct = total > 0 ? Math.min(100, Math.max(0, (Number(value) / Number(total)) * 100)) : 0;
  const barClass = tone ? `pa-progress-bar pa-progress-${tone}` : `pa-progress-bar ${pct >= 100 ? "" : "pa-progress-amber"}`;
  return (
    <div
      className="pa-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      title={percent ? `${count(value)} of ${count(total)}` : `${inr(value)} of ${inr(total)} paid`}
    >
      <div className="pa-progress-track">
        <div className={barClass} style={{ width: pct + "%" }} />
      </div>
      {label && (
        <div className="pa-progress-label">
          {percent ? (
            <>
              <span>{Math.round(pct)}%</span>
              <span>
                {count(value)} of {count(total)}
              </span>
            </>
          ) : (
            <>
              <span>{Math.round(pct)}% paid</span>
              <span>{inr(Number(total) - Number(value))} left</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const EmptyState = ({ icon, title = "Nothing here", children, action, compact }) => (
  <div className={`pa-empty ${compact ? "pa-empty-compact" : ""}`}>
    <div className="pa-empty-icon" aria-hidden="true">
      {icon || <IconInbox size={26} stroke={1.6} />}
    </div>
    <div className="pa-empty-title">{title}</div>
    {children && <div className="pa-empty-body">{children}</div>}
    {action && <div className="pa-empty-action">{action}</div>}
  </div>
);

export const Callout = ({ tone = "blue", icon, title, children, action }) => (
  <div className={`pa-callout pa-callout-${tone}`} role={tone === "red" ? "alert" : undefined}>
    {icon && (
      <span className="pa-callout-icon" aria-hidden="true">
        {icon}
      </span>
    )}
    <div className="pa-callout-body">
      {title && <div className="pa-callout-title">{title}</div>}
      {children}
    </div>
    {action && <div className="pa-callout-action">{action}</div>}
  </div>
);

export const KeyValues = ({ items }) => (
  <div className="pa-kv">
    {items.filter(Boolean).map((it) => (
      <div key={it.label} className="pa-kv-item" style={it.tone ? { background: `var(--pa-${it.tone}-bg)` } : undefined}>
        <div className="pa-kv-label">{it.label}</div>
        <div className="pa-kv-value" style={it.tone ? { color: `var(--pa-${it.tone})` } : undefined}>
          {it.value}
        </div>
        {it.sub && <div className="pa-kv-sub">{it.sub}</div>}
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Dialog + useConfirm                                                 */
/* ------------------------------------------------------------------ */

export const Dialog = ({ open, onClose, title, size = "md", footer, children }) => (
  <Modal isOpen={open} toggle={onClose} size={size} className="pa-modal" centered scrollable>
    <ModalHeader toggle={onClose}>{title}</ModalHeader>
    <ModalBody>
      <div className="pa">{children}</div>
    </ModalBody>
    {footer && (
      <ModalFooter>
        <div className="pa pa-modal-actions">{footer}</div>
      </ModalFooter>
    )}
  </Modal>
);

const settleAsCancelled = (resolver) => {
  const pending = resolver.current;
  resolver.current = null;
  if (pending) pending({ ok: false, value: "" });
};

// const [confirmDialog, confirm] = useConfirm();
// const { ok, value } = await confirm({ title, message, confirmLabel, tone: "danger",
//   input: { label, placeholder, required, multiline, defaultValue }, details: <jsx/> });
// Render {confirmDialog} once in the page. Opening a second confirm while one
// is showing cancels the first ({ ok: false }).
export function useConfirm() {
  const [state, setState] = useState(null);
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);
  const resolver = useRef(null);
  const inputRef = useRef(null);

  // A confirm() still waiting when a new one opens (or the page unmounts)
  // resolves as cancelled, so an awaited confirm() never hangs.
  const confirm = useCallback((opts) => {
    settleAsCancelled(resolver);
    setValue((opts.input && opts.input.defaultValue) || "");
    setTouched(false);
    setState(opts);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  useEffect(() => () => settleAsCancelled(resolver), []);

  const close = (ok) => {
    if (ok && state && state.input && state.input.required && !value.trim()) {
      setTouched(true);
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    const resolve = resolver.current;
    resolver.current = null;
    setState(null);
    if (resolve) resolve({ ok, value: value.trim() });
  };

  const input = state && state.input;
  const invalid = input && input.required && touched && !value.trim();

  const element = (
    <Dialog
      open={Boolean(state)}
      onClose={() => close(false)}
      title={state ? state.title : ""}
      size={state && state.size}
      footer={
        state && (
          <>
            <Button onClick={() => close(false)}>{state.cancelLabel || "Cancel"}</Button>
            <Button variant={state.tone === "danger" ? "danger-solid" : "primary"} onClick={() => close(true)}>
              {state.confirmLabel || "Confirm"}
            </Button>
          </>
        )
      }
    >
      {state && (
        <>
          {state.message && <p className="pa-dialog-message">{state.message}</p>}
          {state.details}
          {input && (
            <div className="pa-field pa-confirm-field">
              <label htmlFor="pa-confirm-input">
                {input.label}
                {input.required && <span className="pa-required"> *</span>}
              </label>
              {input.multiline ? (
                <textarea
                  id="pa-confirm-input"
                  ref={inputRef}
                  className="pa-input"
                  autoFocus
                  placeholder={input.placeholder || ""}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  maxLength={input.maxLength || 500}
                  aria-invalid={Boolean(invalid)}
                />
              ) : (
                <input
                  id="pa-confirm-input"
                  ref={inputRef}
                  className="pa-input"
                  autoFocus
                  placeholder={input.placeholder || ""}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && close(true)}
                  maxLength={input.maxLength || 500}
                  aria-invalid={Boolean(invalid)}
                />
              )}
              {invalid && <div className="pa-field-error">{input.label} is required</div>}
            </div>
          )}
        </>
      )}
    </Dialog>
  );

  return [element, confirm];
}
